/**
 * Workflow Detail View
 * 
 * Shows a single workflow with all tasks and allows completing/skipping tasks.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { workflowAPI } from '../lib/api';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { format, parseISO, isPast } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  UserPlus,
  UserMinus,
  Play,
  CheckCircle2,
  Clock,
  AlertCircle,
  ArrowLeft,
  Loader2,
  XCircle,
  FileText,
  Calendar,
  User,
  SkipForward,
  Upload,
  Check,
  AlertTriangle,
  MapPin,
  Baby,
  ShieldCheck,
  GraduationCap,
  Zap,
} from 'lucide-react';

const WORKFLOW_TYPE_CONFIG = {
  onboarding: { label: 'On-Boarding', icon: UserPlus, color: 'text-green-600', bg: 'bg-green-100' },
  offboarding: { label: 'Off-Boarding', icon: UserMinus, color: 'text-orange-600', bg: 'bg-orange-100' },
  versetzung: { label: 'Versetzung', icon: MapPin, color: 'text-blue-600', bg: 'bg-blue-100' },
  probezeit: { label: 'Probezeit', icon: Clock, color: 'text-purple-600', bg: 'bg-purple-100' },
  mutterschutz: { label: 'Mutterschutz', icon: Baby, color: 'text-pink-600', bg: 'bg-pink-100' },
  zertifikat: { label: 'Zertifikate', icon: ShieldCheck, color: 'text-amber-600', bg: 'bg-amber-100' },
  pflichtschulung: { label: 'Schulungen', icon: GraduationCap, color: 'text-teal-600', bg: 'bg-teal-100' },
  vertragsende: { label: 'Vertragsende', icon: FileText, color: 'text-red-600', bg: 'bg-red-100' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'dd.MM.yyyy', { locale: de });
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'dd.MM.yyyy HH:mm', { locale: de });
  } catch {
    return dateStr;
  }
};

const TASK_STATUS_CONFIG = {
  pending: { label: 'Ausstehend', color: 'bg-yellow-500', icon: Clock },
  in_progress: { label: 'In Bearbeitung', color: 'bg-blue-500', icon: Play },
  completed: { label: 'Erledigt', color: 'bg-green-500', icon: CheckCircle2 },
  skipped: { label: 'Übersprungen', color: 'bg-gray-500', icon: SkipForward },
};

const WORKFLOW_STATUS_CONFIG = {
  active: { label: 'Aktiv', color: 'bg-blue-500', icon: Play },
  completed: { label: 'Abgeschlossen', color: 'bg-green-500', icon: CheckCircle2 },
  cancelled: { label: 'Abgebrochen', color: 'bg-gray-500', icon: XCircle },
};

export default function WorkflowDetail() {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Complete task dialog
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskNotes, setTaskNotes] = useState('');
  const [documentUrl, setDocumentUrl] = useState('');
  const [completing, setCompleting] = useState(false);
  
  // Skip task dialog
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [skipNotes, setSkipNotes] = useState('');
  const [skipping, setSkipping] = useState(false);
  
  // Cancel workflow dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const loadWorkflow = useCallback(async () => {
    setLoading(true);
    try {
      const response = await workflowAPI.getWorkflow(workflowId);
      setWorkflow(response.data);
    } catch (error) {
      console.error('Error loading workflow:', error);
      toast.error('Workflow nicht gefunden');
      navigate('/workflows');
    } finally {
      setLoading(false);
    }
  }, [workflowId, navigate]);

  useEffect(() => {
    loadWorkflow();
  }, [loadWorkflow]);

  const openCompleteDialog = (task) => {
    setSelectedTask(task);
    setTaskNotes('');
    setDocumentUrl(task.document_url || '');
    setCompleteDialogOpen(true);
  };

  const openSkipDialog = (task) => {
    setSelectedTask(task);
    setSkipNotes('');
    setSkipDialogOpen(true);
  };

  const handleCompleteTask = async () => {
    if (selectedTask.requires_document && !documentUrl.trim()) {
      toast.error('Bitte Dokument-URL angeben');
      return;
    }
    
    setCompleting(true);
    try {
      const result = await workflowAPI.completeTask(workflowId, selectedTask.id, {
        notes: taskNotes || null,
        document_url: documentUrl || null,
      });
      
      toast.success('Aufgabe abgeschlossen');
      if (result.data.workflow_completed) {
        toast.success('Workflow vollständig abgeschlossen!', { duration: 5000 });
      }
      setCompleteDialogOpen(false);
      loadWorkflow();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Abschließen');
    } finally {
      setCompleting(false);
    }
  };

  const handleSkipTask = async () => {
    setSkipping(true);
    try {
      const result = await workflowAPI.skipTask(workflowId, selectedTask.id, skipNotes || null);
      toast.success('Aufgabe übersprungen');
      if (result.data.workflow_completed) {
        toast.success('Workflow vollständig abgeschlossen!', { duration: 5000 });
      }
      setSkipDialogOpen(false);
      loadWorkflow();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Überspringen');
    } finally {
      setSkipping(false);
    }
  };

  const handleCancelWorkflow = async () => {
    setCancelling(true);
    try {
      await workflowAPI.cancelWorkflow(workflowId);
      toast.success('Workflow abgebrochen');
      setCancelDialogOpen(false);
      loadWorkflow();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Abbrechen');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32" />
          <Skeleton className="h-64" />
        </div>
      </DashboardLayout>
    );
  }

  if (!workflow) {
    return null;
  }

  const statusConfig = WORKFLOW_STATUS_CONFIG[workflow.status];
  const StatusIcon = statusConfig.icon;
  const completedTasks = workflow.tasks?.filter(t => t.status === 'completed' || t.status === 'skipped').length || 0;
  const totalTasks = workflow.tasks?.length || 0;

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="workflow-detail-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/workflows')} className="self-start">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              {(() => {
                const tc = WORKFLOW_TYPE_CONFIG[workflow.workflow_type] || WORKFLOW_TYPE_CONFIG.onboarding;
                const TypeIcon = tc.icon;
                return (
                  <div className={`p-2 rounded-lg ${tc.bg} shrink-0`}>
                    <TypeIcon className={`h-5 w-5 ${tc.color}`} />
                  </div>
                );
              })()}
              <div className="min-w-0">
                <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2 flex-wrap">
                  <span className="truncate">{workflow.employee_name}</span>
                  <Badge className={`${statusConfig.color} text-white`}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {statusConfig.label}
                  </Badge>
                </h1>
                <p className="text-muted-foreground truncate">{workflow.template_name}</p>
              </div>
            </div>
          </div>
          {workflow.status === 'active' && (
            <Button
              variant="outline"
              className="text-red-500 w-full md:w-auto"
              onClick={() => setCancelDialogOpen(true)}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Abbrechen
            </Button>
          )}
        </div>

        {/* Progress Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Gestartet: {formatDateTime(workflow.started_at)}
                  </span>
                  {workflow.started_by_name && (
                    <span className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      von {workflow.started_by_name}
                    </span>
                  )}
                </div>
                {workflow.completed_at && (
                  <p className="text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" />
                    Abgeschlossen: {formatDateTime(workflow.completed_at)}
                  </p>
                )}
              </div>
              <div className="md:text-right">
                <div className="flex items-center md:justify-end gap-2 mb-2 flex-wrap">
                  <span className="text-2xl font-bold">{workflow.progress}%</span>
                  <span className="text-muted-foreground">
                    ({completedTasks}/{totalTasks} Aufgaben)
                  </span>
                </div>
                <Progress value={workflow.progress} className="h-3 w-full md:w-[200px]" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tasks List */}
        <Card>
          <CardHeader>
            <CardTitle>Aufgaben</CardTitle>
            <CardDescription>
              Schließen Sie alle Aufgaben ab, um den Workflow zu beenden.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {workflow.tasks?.sort((a, b) => a.order - b.order).map((task, index) => {
                const taskStatus = TASK_STATUS_CONFIG[task.status];
                const TaskIcon = taskStatus.icon;
                const isOverdue = task.status === 'pending' && task.deadline_date && isPast(parseISO(task.deadline_date));
                const canComplete = workflow.status === 'active' && task.status === 'pending';

                return (
                  <div
                    key={task.id}
                    className={`p-4 border rounded-lg ${
                      task.status === 'completed' ? 'bg-green-50 border-green-200' :
                      task.status === 'skipped' ? 'bg-gray-50 border-gray-200' :
                      isOverdue ? 'bg-red-50 border-red-200' :
                      'bg-white'
                    }`}
                    data-testid={`task-${task.id}`}
                  >
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        task.status === 'completed' ? 'bg-green-500 text-white' :
                        task.status === 'skipped' ? 'bg-gray-400 text-white' :
                        'bg-gray-200'
                      }`}>
                        {task.status === 'completed' ? (
                          <Check className="h-4 w-4" />
                        ) : task.status === 'skipped' ? (
                          <SkipForward className="h-4 w-4" />
                        ) : (
                          <span className="text-sm font-medium">{index + 1}</span>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h4 className={`font-medium ${task.status !== 'pending' ? 'line-through text-muted-foreground' : ''}`}>
                              {task.name}
                            </h4>
                            {task.description && (
                              <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                            )}
                          </div>
                          <Badge className={`${taskStatus.color} text-white flex-shrink-0`}>
                            <TaskIcon className="h-3 w-3 mr-1" />
                            {taskStatus.label}
                          </Badge>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
                          {task.assigned_role && (
                            <span className="flex items-center gap-1 bg-secondary px-2 py-0.5 rounded">
                              <User className="h-3 w-3" />
                              {task.assigned_role}
                            </span>
                          )}
                          {task.deadline_date && (
                            <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-500 font-medium' : ''}`}>
                              <Calendar className="h-3 w-3" />
                              Frist: {formatDate(task.deadline_date)}
                              {isOverdue && <AlertTriangle className="h-3 w-3 ml-1" />}
                            </span>
                          )}
                          {task.requires_document && (
                            <span className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                              <FileText className="h-3 w-3" />
                              {task.document_name || 'Dokument erforderlich'}
                            </span>
                          )}
                        </div>
                        
                        {/* Completed info */}
                        {(task.status === 'completed' || task.status === 'skipped') && (
                          <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
                            <p>
                              {task.status === 'completed' ? 'Erledigt' : 'Übersprungen'} von {task.completed_by_name} am {formatDateTime(task.completed_at)}
                            </p>
                            {task.notes && <p className="mt-1">Notiz: {task.notes}</p>}
                            {task.document_url && (
                              <a 
                                href={task.document_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-primary hover:underline flex items-center gap-1 mt-1"
                              >
                                <FileText className="h-3 w-3" />
                                Dokument ansehen
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Actions */}
                      {canComplete && (
                        <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto sm:ml-auto">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openSkipDialog(task)}
                            data-testid={`skip-task-${task.id}`}
                          >
                            <SkipForward className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => openCompleteDialog(task)}
                            data-testid={`complete-task-${task.id}`}
                            className="flex-1 sm:flex-none"
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Erledigt
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Complete Task Dialog */}
        <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Aufgabe abschließen</DialogTitle>
              <DialogDescription>
                {selectedTask?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {selectedTask?.requires_document && (
                <div className="space-y-2">
                  <Label>
                    Dokument-URL *
                    {selectedTask.document_name && (
                      <span className="text-muted-foreground ml-1">({selectedTask.document_name})</span>
                    )}
                  </Label>
                  <Input
                    value={documentUrl}
                    onChange={(e) => setDocumentUrl(e.target.value)}
                    placeholder="https://..."
                    data-testid="document-url-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Laden Sie das Dokument hoch und fügen Sie den Link hier ein.
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Notiz (optional)</Label>
                <Textarea
                  value={taskNotes}
                  onChange={(e) => setTaskNotes(e.target.value)}
                  placeholder="Optionale Anmerkungen..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCompleteDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={handleCompleteTask} 
                disabled={completing || (selectedTask?.requires_document && !documentUrl.trim())}
              >
                {completing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                Abschließen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Skip Task Dialog */}
        <Dialog open={skipDialogOpen} onOpenChange={setSkipDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Aufgabe überspringen</DialogTitle>
              <DialogDescription>
                {selectedTask?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                <AlertCircle className="h-4 w-4 inline mr-2" />
                Übersprungene Aufgaben werden als nicht erledigt markiert.
              </div>
              
              <div className="space-y-2">
                <Label>Grund (optional)</Label>
                <Textarea
                  value={skipNotes}
                  onChange={(e) => setSkipNotes(e.target.value)}
                  placeholder="Warum wird diese Aufgabe übersprungen?"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSkipDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSkipTask} disabled={skipping} variant="secondary">
                {skipping ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <SkipForward className="h-4 w-4 mr-2" />
                )}
                Überspringen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel Workflow Dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Workflow abbrechen</DialogTitle>
              <DialogDescription>
                Sind Sie sicher, dass Sie diesen Workflow abbrechen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
                Nein, zurück
              </Button>
              <Button onClick={handleCancelWorkflow} disabled={cancelling} variant="destructive">
                {cancelling ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <XCircle className="h-4 w-4 mr-2" />
                )}
                Ja, abbrechen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
